import { useEffect, useState } from 'react';

import cn from 'classnames';
import { PartnersCodes } from 'constants/partners';
import { PartnersConfig } from 'constants/partners-config';
import { useDispatch, useSelector } from 'react-redux';

import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import { SearchInput, Select, ToggleLabel } from 'components/FormComponents';
import * as Integration from 'components/Integrations/index';

import { revokePartnerToken } from 'api/partner-token-version-slice';
import {
  connectBullhorn,
  connectJobAdder,
  connectLever,
  connectLivehire,
  disconnectBullhorn,
  disconnectJobAdder,
  disconnectLever,
  disconnectLivehire,
  getPartnersConfig,
  selectPartnersConfig,
  updatePartnersConfig,
} from 'api/partners-config-slice';
import { useGetCompanyUsersQuery } from 'api/user-api';

import styles from './Integrations.module.scss';

export const Integrations = ({ user, company, setLoading = () => {}, popUpMessage = () => {} }) => {
  const dispatch = useDispatch();

  const partners = useSelector(selectPartnersConfig);

  const { data: companyUsers } = useGetCompanyUsersQuery(user?.id, {
    refetchOnMountOrArgChange: true,
  });

  const onConnectJobAdder = code => dispatch(connectJobAdder(code));
  const onDisconnectJobAdder = () => dispatch(disconnectJobAdder());
  const onConnectBullhorn = params => dispatch(connectBullhorn(params));
  const onDisconnectBullhorn = () => dispatch(disconnectBullhorn());
  const onConnectLever = code => dispatch(connectLever(code));
  const onDisconnectLever = () => dispatch(disconnectLever());
  const onConnectLivehire = params => dispatch(connectLivehire(params));
  const onDisconnectLivehire = () => dispatch(disconnectLivehire());
  const onGetPartnersConfig = () => dispatch(getPartnersConfig());
  const onPartnersConfigUpdate = params => dispatch(updatePartnersConfig(params));
  const onRevokePartnerToken = partner => dispatch(revokePartnerToken(partner));

  const [useIntegrationUser, setUseIntegrationUser] = useState(!!partners.config.user_id);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!partners.fetched) return;

    const shouldUseIntegrationUser = !!partners.config.user_id;
    setUseIntegrationUser(shouldUseIntegrationUser);

    // If shouldUseIntegrationUser is true and we already have a user_id, don't change anything
    if (shouldUseIntegrationUser && partners?.config?.user_id) return;

    const value = shouldUseIntegrationUser ? company.creator_id : null;
    setLoading(true);
    onPartnersConfigUpdate({ user_id: value }).then(() => setLoading(false));
  }, [partners.fetched]);

  useEffect(() => {
    setLoading(true);
    onGetPartnersConfig().then(_ => setLoading(false));
  }, []);

  const handleTokenRevoke = async partner => {
    setLoading(true);
    await onRevokePartnerToken(partner);
    setLoading(false);
  };

  const getPartnerIntegration = config => {
    if (!partners.fetched) return;

    switch (config.name) {
      case PartnersCodes.AccountApi:
        return (
          <Integration.AccountApiIntegration
            key={config.name}
            config={config}
            integration={partners.account_api}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.TAS:
        return (
          <Integration.TasIntegration
            key={config.name}
            config={config}
            integration={partners.tas}
            setLoading={setLoading}
            updatePartnersConfig={onPartnersConfigUpdate}
          />
        );
      case PartnersCodes.JobAdder:
        return (
          <Integration.JobAdderIntegration
            key={config.name}
            config={config}
            integration={partners.job_adder}
            connect={onConnectJobAdder}
            disconnect={onDisconnectJobAdder}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
          />
        );
      case PartnersCodes.JazzHR:
        return (
          <Integration.JazzIntegration
            key={config.name}
            config={config}
            integration={partners.jazzhr}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.Teamtailor:
        return (
          <Integration.TeamtailorIntegration
            key={config.name}
            config={config}
            integration={partners.teamtailor}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.Bullhorn:
        return (
          <Integration.BullhornIntegration
            key={config.name}
            config={config}
            integration={partners.bullhorn}
            connect={onConnectBullhorn}
            disconnect={onDisconnectBullhorn}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.Onboarded:
        return (
          <Integration.OnboardedIntegration
            key={config.name}
            config={config}
            integration={partners.onboarded}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.Lever:
        return (
          <Integration.LeverIntegration
            key={config.name}
            config={config}
            integration={partners.lever}
            connect={onConnectLever}
            disconnect={onDisconnectLever}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
            updatePartnersConfig={onPartnersConfigUpdate}
          />
        );
      case PartnersCodes.UKG:
        return (
          <Integration.UkgIntegration
            key={config.name}
            config={config}
            integration={partners.ukg}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.BambooHR:
        return (
          <Integration.BambooHRIntegration
            key={config.name}
            config={config}
            integration={partners.bambooHR}
            setLoading={setLoading}
            updatePartnersConfig={onPartnersConfigUpdate}
          />
        );
      case PartnersCodes.PageUp:
        return (
          <Integration.PageUpIntegration
            key={config.name}
            config={config}
            integration={partners.pageup}
            updatePartnersConfig={onPartnersConfigUpdate}
            setLoading={setLoading}
          />
        );
      case PartnersCodes.Livehire:
        return (
          <Integration.LivehireIntegration
            key={config.name}
            config={config}
            integration={partners.livehire}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
            connectLivehire={onConnectLivehire}
            disconnectLivehire={onDisconnectLivehire}
          />
        );
      case PartnersCodes.SmartRecruiters:
        return (
          <Integration.SmartRecruitersIntegration
            key={config.name}
            config={config}
            integration={partners.smartrecruiters}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
            connectLivehire={onConnectLivehire}
            disconnectLivehire={onDisconnectLivehire}
          />
        );
    }
  };

  const updateUser = async e => {
    setLoading(true);
    await onPartnersConfigUpdate({ user_id: e.target.value });
    setLoading(false);
  };

  const handleUseIntegrationUserChange = async e => {
    setUseIntegrationUser(e.target.checked);
    if (!e.target.checked) {
      await onPartnersConfigUpdate({ user_id: null });
    } else {
      await updateUser({ target: { value: partners?.config?.user_id || company.creator_id } });
    }
  };

  const hanldePartnersConfigUpdate = async e => {
    const name = e.target.name;
    const value = e.target.checked ?? e.target.value;

    setLoading(true);
    await onPartnersConfigUpdate({ [name]: value });
    setLoading(false);
  };

  return (
    <div>
      <div>
        <div
          className={cn(
            'card--large',
            'card',
            'card-with-border',
            'u-margin-bottom',
            'u-padding--large',
          )}>
          <div className={cn('d-flex', 'justify-content-between', 'align-items-center')}>
            <div>
              <h1 className="title-2">Integrations</h1>
              <p className="t-small u-margin-0">
                Configure and activate integrations between Checkmate and other software platforms.
                To request an integration, please contact{' '}
                <a className="u-link dark" href="mailto:team@checkmate.tech">
                  team@checkmate.tech
                </a>
                .
              </p>
            </div>
            <SearchInput
              placeholder="Search for an integration"
              className="u-width-25"
              onChange={setSearchQuery}
            />
          </div>
          <EditableSettings
            title="Settings"
            className="u-margin-top--large u-margin-bottom--0"
            startOpen={true}>
            <ToggleLabel
              label="Require authentication for viewing result files"
              inputProps={{
                name: 'use_auth_on_result_files',
                checked: partners.config.use_auth_on_result_files,
                onChange: hanldePartnersConfigUpdate,
              }}
            />
            <div className="u-divider"></div>
            <ToggleLabel
              label="Use integration user"
              tooltip="If disabled, main company user will be used. If launcher user email is provided by partner, it will have presedence over this"
              inputProps={{
                checked: useIntegrationUser,
                onChange: handleUseIntegrationUserChange,
              }}
            />
            {useIntegrationUser && (
              <Select
                name="user_id"
                value={partners.config.user_id}
                inputProps={{ onChange: updateUser }}
                className="u-margin-top--small">
                {companyUsers?.map(user => (
                  <Select.Item key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} • {user.email}
                  </Select.Item>
                ))}
              </Select>
            )}
          </EditableSettings>
        </div>
      </div>
      <div className={styles.cardsWrapper}>
        {PartnersConfig.filter(partner =>
          partner.title.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase()),
        ).map(config => getPartnerIntegration(config))}
      </div>
    </div>
  );
};
